import { createContext, useCallback, useContext, useMemo } from 'react'
import { useLocalStorage } from 'usehooks-ts'
import type { CurrencyFormats } from '@velasca/types'
import { formatMoney } from '@velasca/utils/format-money'

interface ExchangeRatesProviderProps {
  children: React.ReactNode
  currencyFormat: CurrencyFormats
  defaultFormat: string
  defaultCurrency: string
  active?: boolean
}

type Currency = string | null

const ExchangeRatesContext = createContext<{
  setCurrency: React.Dispatch<React.SetStateAction<Currency>>
  convert: (priceRaw: number) => string
  currency: any
}>({
  setCurrency: () => {},
  convert: (priceRaw) => `${priceRaw}`,
  currency: '',
})

export const useExchangeRates = () => useContext(ExchangeRatesContext)

const ExchangeRatesProvider = ({
  children,
  currencyFormat,
  defaultFormat,
  defaultCurrency,
  active = false,
}: ExchangeRatesProviderProps) => {
  const [storedCurrency, setStoredCurrency] = useLocalStorage<Currency>(
    'currency',
    null,
  )

  const [, setIsCurrencyAutomaticallySelected] = useLocalStorage<
    boolean | null
  >('isCurrencyAutomaticallySelected', null)

  const currency: Currency = active ? storedCurrency : null

  const setCurrency = useCallback(
    (newCurrency: React.SetStateAction<Currency>) => {
      if (active) {
        setStoredCurrency(newCurrency)
        setIsCurrencyAutomaticallySelected(false)
      }
    },
    [active, setStoredCurrency, setIsCurrencyAutomaticallySelected],
  )

  const selectedCurrency = useMemo(() => {
    return currency || defaultCurrency || null
  }, [currency, defaultCurrency])

  // TODO: always pass centesimal pricing ($10 = 1000) as number. In this way we can abstract from actual money_format
  // The reason this was done this way it's because the original code was reading the price from HTML printed by liquid
  // so it *needed* the format. Using React, the format is not needed anymore because we can convert *before* we print
  const convert = useCallback(
    (priceRaw: number) => {
      if (!active || !defaultCurrency || !selectedCurrency) {
        return formatMoney(priceRaw, defaultFormat)
      }

      const selectedCurrencyFormat =
        window.Currency.moneyFormats[selectedCurrency][currencyFormat] ||
        '{{amount}}'

      const cents = window.Currency.convert(
        priceRaw,
        defaultCurrency,
        selectedCurrency,
      )

      return formatMoney(cents, selectedCurrencyFormat)
    },
    [active, defaultCurrency, selectedCurrency, currencyFormat, defaultFormat],
  )

  return (
    <ExchangeRatesContext.Provider
      value={{
        setCurrency,
        convert,
        currency: selectedCurrency,
      }}
    >
      {children}
    </ExchangeRatesContext.Provider>
  )
}

export default ExchangeRatesProvider
